/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useRef, useState } from "react";
import { ReceptionReference } from "$types/ReceptionReference";
import { OrderReference } from "$types/OrderReference";
import { ProductReference } from "$types/ProductReference";
import ReferenceType from "$types/ReferenceType";
import { ProductReferenceList } from "$types/ProductReferenceList";
import useAuth from "./useAuth";
import { addProduct, editProduct } from "$api/productApi";
import usePreviousReferencings from "./usePreviousReferencings";
import { queryClient } from "$utils/queryClient";
import { getProductByReference } from "$services/Products.service";
import { CacheInvalidationAction } from "$utils/cache";

const RECEPTION_REFERENCING_UNIT_NAME = "Colis et/ou palette";

/** Catalog refs sometimes contain stray spaces (e.g. `…1719 _OAC`). */
const normalizeProductReference = (reference?: string | null) =>
  String(reference || "").replace(/\s+/g, "");

function overrideValues<T>(initialValues: T, newValues: Partial<T>) {
  const res = initialValues;
  // override res[key] by newValues[key] if present
  Object.keys(newValues).forEach((key) => {
    res[key] = newValues[key] ? newValues[key] : res[key];
  });
  return res;
}

const normalizeOrders = (orders: any): any[] => {
  if (!orders) return [];
  if (orders.data && Array.isArray(orders.data)) {
    return orders.data.map((order: any) => ({
      id: order.id,
      ...order.attributes,
    }));
  }
  if (Array.isArray(orders)) {
    return orders;
  }
  return [];
};

function useReferencing(companyId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const {
    addToPreviousProductListRefs,
    deletePreviousOrderRef,
    deletePreviousProductListRef,
    getPreviousOrderRefFromProductList,
    getPreviousProductListFromOrderRef,
  } = usePreviousReferencings();
  const { token } = useAuth();

  /**
   * Change an product referencing block to order referencing block
   * @param prependOrderRef react-hook-form prepend method
   * @param productRefIndex index of the productRefList behind changed
   * @param productReferenceList productRefList to change
   * @param removeProductRefList react-hook-form remove method
   */
  const fromProductToOrderReferencing = async (
    referencingCategory: any,
    prependOrderRef: (orderRef: OrderReference) => void,
    productRefIndex: number,
    productReferenceList: ProductReferenceList,
    removeProductRefList: (index: number) => void,
    setConvertedReferences?: (
      refs: string[] | ((prev: string[]) => string[])
    ) => void
  ) => {
    removeProductRefList(productRefIndex);

    // Track converted references
    if (setConvertedReferences) {
      const productRefs = productReferenceList.productReferences
        .map((pr) => pr.productReference)
        .filter((ref) => ref);
      setConvertedReferences((prev) => [...prev, ...productRefs]);
    }

    // Check if there's a previous OrderReference for this conversion
    const previousRef =
      getPreviousOrderRefFromProductList(productReferenceList);
    if (previousRef) {
      deletePreviousOrderRef(previousRef);
      prependOrderRef(previousRef);
      return;
    }

    const findOrder = (reference: string) => {
      // Find the product reference that contains the matching suborder
      const productRef = productReferenceList.productReferences.find((pr) => {
        const normalizedOrders = normalizeOrders(pr.orders);
        return normalizedOrders.some((o) =>
          o.suborders.some((s) => s.reference === reference)
        );
      });

      if (!productRef) return null;

      // Find the specific order that contains the matching suborder
      const normalizedOrders = normalizeOrders(productRef.orders);
      const order = normalizedOrders.find((o) =>
        o.suborders.some((s) => s.reference === reference)
      );

      if (!order) return null;

      return order;
    };

    const populatedSuborders = productReferenceList?.suborders.map((s) => ({
      ...s,
      order:
        findOrder(s.reference) ||
        normalizeOrders(productReferenceList.productReferences[0]?.orders)?.[0],
    }));

    addToPreviousProductListRefs(productReferenceList);

    const orderRef = getDefaultOrderReference(
      referencingCategory,
      populatedSuborders,
      {
        id: productReferenceList.id,
        referenceType: ReferenceType.ORDER,
        suborders: populatedSuborders,
        productReference: null,
        receptionProduct: {
          categories: referencingCategory ? [referencingCategory.id] : [],
          description: null,
          designation: null,
          quantity: 1,
          reference: null,
          unit: RECEPTION_REFERENCING_UNIT_NAME,
        },
      }
    );

    prependOrderRef(orderRef);
  };

  /**
   * Change an order referencing block to product referencing block
   * @param orderRefIndex index of the orderRef behind changed
   * @param prependProductRefList react-hook-form prepend method
   * @param receptionReference receptionReference from which the orderRef is from
   * @param removeOrderRef productRefList to change
   */
  const fromOrderToProductReferencing = (
    orderRefIndex: number,
    orderRef: OrderReference,
    prependProductRefList: (list: ProductReferenceList) => void,
    receptionReference: ReceptionReference,
    removeOrderRef: (index: number) => void,
    setConvertedReferences?: (
      refs: string[] | ((prev: string[]) => string[])
    ) => void
  ) => {
    removeOrderRef(orderRefIndex);

    if (setConvertedReferences && orderRef.productReference) {
      setConvertedReferences((prev) => [...prev, orderRef.productReference]);
    }

    const allSuborders = receptionReference.suborders || [];

    // Create ONE consolidated ProductReferenceList with ALL suborders
    const normalizedOrders = normalizeOrders(receptionReference.orders);

    const allProductReferences = allSuborders.flatMap((suborder) =>
      suborder.suborderProducts.map((product) => {
        const productRef = getDefaultProductReference(allSuborders, {
          orders:
            normalizedOrders.find((o) =>
              o.suborders.some(
                (sub) =>
                  sub.reference === suborder.reference &&
                  sub.suborderProducts.some(
                    (p) => p.reference === product.reference
                  )
              )
            ) || [],
          originalProductReference: product.reference,
          referenceType: ReferenceType.PRODUCT,
          suborders: allSuborders,
        });
        return {
          ...productRef,
          orders: normalizedOrders,
        };
      })
    );

    const consolidatedProductRefList = {
      id: allSuborders[0].id,
      suborders: allSuborders,
      productReferences: allProductReferences.sort((p1, p2) =>
        p1.originalProductReference.localeCompare(p2.originalProductReference)
      ),
    };

    prependProductRefList(consolidatedProductRefList);
  };

  const getDefaultOrderReference = (
    referencingCategory: any,
    suborders: any[],
    values: Partial<OrderReference> = {}
  ): OrderReference => {
    const defaultValues = {
      receptionProduct: {
        categories: referencingCategory ? [referencingCategory.id] : [],
        description: null,
        designation: null,
        quantity: 1,
        reference: null,
        unit: RECEPTION_REFERENCING_UNIT_NAME,
      },
      orders: Array.from(new Set(suborders.map((s) => s.order))),
      originalProductReference: null,
      productReference: null,
      referenceType: ReferenceType.ORDER,
      suborders,
      suborderReferences: suborders.map((s) => s.reference),
      id: suborders[0].id,
    } as OrderReference;
    const res = overrideValues(defaultValues, values);
    return res;
  };

  const getDefaultProductReference = (
    suborders: any[],
    values: Partial<ProductReference> = {}
  ): ProductReference => {
    const defaultValues: ProductReference = {
      orders: Array.from(new Set(suborders.map((s) => s.order))),
      originalProductReference: null,
      productReference: null,
      referenceType: ReferenceType.PRODUCT,
      suborders,
      suborderReferences: suborders.map((s) => s.reference),
      id: suborders[0].id,
    };
    const res = overrideValues(defaultValues, values);
    return res;
  };

  /**
   * Returns true if the newReference is the same as the previousReference too.
   */
  const isReferenceUnique = async (
    previousReference: string,
    newReference: string,
    isInReferenceCategory = undefined
  ) => {
    if (!newReference || previousReference === newReference) {
      return true;
    }
    // Include isInReferenceCategory in cache key to avoid stale/mismatched results
    const cacheKey = [
      CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
      companyId,
      newReference,
      isInReferenceCategory,
    ];
    let data;
    data = (await queryClient.getQueryData(cacheKey)) as any;

    if (!data) {
      data = await queryClient.fetchQuery({
        queryKey: cacheKey,

        queryFn: () =>
          getProductByReference({
            company: companyId,
            reference: newReference,
            ...(isInReferenceCategory !== undefined
              ? { isInReferenceCategory }
              : {}),
          }),

        gcTime: 5 * 60 * 1000,
      });
    }

    if (!data || data.length === 0) return true;
    const existingProduct = data[0];

    // If it's the same product (edit), it's allowed
    if (existingProduct.reference === previousReference) return true;

    // If the existing product has the "REFERENCEMENT" category (case-insensitive and handling accents), we allow grouping (reusing the reference)
    const hasReferencementCategory = existingProduct.categories?.some(
      (c: any) => c?.title && /^r[eéèê]f[eéèê]rencement$/i.test(c.title)
    );

    if (hasReferencementCategory) return true;

    // Otherwise (standard catalogue product or another category), it's forbidden
    return false;
  };

  // Debounced version for form validation (avoids API call on every keystroke)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedIsReferenceUnique = useCallback(
    (
      previousReference: string,
      newReference: string,
      isInReferenceCategory?: boolean
    ): Promise<boolean> =>
      new Promise((resolve) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
          const result = await isReferenceUnique(
            previousReference,
            newReference,
            isInReferenceCategory
          );
          resolve(result);
        }, 400);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId]
  );

  const fetchProductList = useCallback(
    async (wantedList) => {
      setIsLoading(true);
      const productList = await Promise.all(
        wantedList
          .filter((r) => !!r.productReference)
          .map((r) => {
            let data;
            data = queryClient.getQueryData([
              CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
              companyId,
              r.productReference,
            ]) as any;
            if (!data) {
              data = queryClient.fetchQuery({
                queryKey: [
                  CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
                  companyId,
                  r.productReference,
                ],

                queryFn: () =>
                  getProductByReference({
                    company: companyId,
                    reference: r.productReference,
                  }),

                gcTime: 5 * 60 * 1000,
              });
            }

            return data;
          })
          .flat()
      );
      setIsLoading(false);
      return productList.flat();
    },
    [companyId]
  );

  const processOrderRefProducts = async (
    orderReferences: OrderReference[],
    companyId: string,
    callback: (index: number, product: any) => void
  ) => {
    // 1. Group by unique reference to prevent race conditions during creation
    const uniqueOrderReferencesMap = new Map<string, OrderReference>();
    const indicesByReference = new Map<string, number[]>();

    orderReferences.forEach((ref, index) => {
      if (ref.productReference) {
        if (!uniqueOrderReferencesMap.has(ref.productReference)) {
          uniqueOrderReferencesMap.set(ref.productReference, ref);
        }
        const indices = indicesByReference.get(ref.productReference) || [];
        indices.push(index);
        indicesByReference.set(ref.productReference, indices);
      }
    });

    const uniqueOrderReferences = Array.from(uniqueOrderReferencesMap.values());

    // 2. Fetch products for unique references
    const productList = await fetchProductList(uniqueOrderReferences);

    // 3. Process each UNIQUE reference once
    await Promise.all(
      uniqueOrderReferences.map(async (orderReference) => {
        const { productReference } = orderReference;
        const flatProducts = productList.flat().filter(Boolean);
        let product = flatProducts.find(
          (p) => p.reference === productReference && p.company === companyId
        );
        // Match ignoring stray spaces in catalog refs
        if (!product) {
          const wanted = normalizeProductReference(productReference);
          product = flatProducts.find(
            (p) =>
              normalizeProductReference(p.reference) === wanted &&
              p.company === companyId
          );
        }
        // Exact fetch misses spaced catalog refs — retry common variant
        if (!product && productReference) {
          const spacedOac = productReference.replace(/_OAC$/i, " _OAC");
          if (spacedOac !== productReference) {
            const alt = await getProductByReference({
              company: companyId,
              reference: spacedOac,
            });
            product = (Array.isArray(alt) ? alt : []).find(
              (p) => p.company === companyId
            );
          }
        }
        // Already linked on the reception reference
        const existingProductId =
          orderReference.productId ||
          (orderReference as any).receptionProduct?.id ||
          (orderReference as any).receptionProduct?._id;
        if (!product && existingProductId) {
          product = { _id: existingProductId, id: existingProductId };
        }

        const receptionProduct = orderReference.receptionProduct;
        const productId = product?._id || product?.id;

        let res: any;
        if (productId) {
          res = await editProduct(token, receptionProduct || {}, productId);
        } else {
          const designation = String(
            receptionProduct?.designation || productReference || ""
          ).trim();
          const unit = String(
            receptionProduct?.unit || RECEPTION_REFERENCING_UNIT_NAME
          ).trim();
          if (!designation) {
            throw new Error(
              `Produit « ${productReference} » : désignation obligatoire pour créer la référence catalogue.`
            );
          }
          res = await addProduct(token, {
            ...(receptionProduct || {}),
            reference: productReference,
            designation,
            unit,
            company: companyId,
            isReferenced: true,
          });
        }

        let savedProduct = null;
        if (res?.error) {
          throw new Error(
            `Error while adding/editing product ${productReference} for order referencing: ${res.error?.message}`
          );
        } else {
          const {
            data: { data },
          } = res;
          savedProduct = data;
        }

        // 4. Call callback for ALL original indices associated with this reference
        if (savedProduct) {
          const indices = indicesByReference.get(productReference) || [];
          indices.forEach((originalIndex) => {
            callback(originalIndex, savedProduct);
          });
        }
      })
    );
  };

  /**
   * Est-ce que cette fonction a encore vocation à exister ?
   * De ce que j'ai compris il n'y a plus de checks à faire sur le référencement à l'article / Plus de produit a créer ...
   * Je met donc en commentaire les appels ...
   */
  const processProductRefProducts = async (
    productReferenceLists: ProductReferenceList[],
    callback: (index: number, prlIndex: number, product: any) => void
  ) => {
    // const productList = await fetchProductList(productReferenceLists);
    // await Promise.all(
    //   productReferenceLists.flatMap((prl, prlIndex) =>
    //     prl.productReferences.map(async (productReference, index) => {
    //       const product = productList
    //         ?.flat()
    //         .find(
    //           (p) =>
    //             p.reference === productReference.productReference &&
    //             p.company === companyId
    //         );
    //       if (product) return; // The product already exists: no update or creation
    //       const originalProduct = productList
    //         ?.flat()
    //         .find(
    //           (p) =>
    //             p.reference === productReference.originalProductReference &&
    //             p.company === companyId
    //         );
    //       const { _id, ...originalProductWithoutId } = originalProduct;
    //       const res: any = await addProduct(token, {
    //         ...originalProductWithoutId, // copy the original product
    //         categories: [(await getReferencingCategory(companyId)).id], // force to referencing category
    //         reference: productReference.productReference,
    //         isReferenced: true,
    //       });
    //       if (res?.error) {
    //         // TODO: not sure about this error handler
    //         throw new Error(
    //           `Error while adding/editing product ${productReference} for product referencing: ${res.error?.message}`
    //         );
    //       } else {
    //         const {
    //           data: { data: product },
    //         } = res;
    //         callback(index, prlIndex, product);
    //       }
    //     })
    //   )
    // );
  };

  return {
    deletePreviousOrderRef,
    deletePreviousProductListRef,
    getPreviousOrderRefFromProductList,
    getPreviousProductListFromOrderRef,
    fromProductToOrderReferencing,
    fromOrderToProductReferencing,
    getDefaultOrderReference,
    getDefaultProductReference,
    isReferenceUnique,
    debouncedIsReferenceUnique,
    processOrderRefProducts,
    processProductRefProducts,
    isLoading,
  };
}

export default useReferencing;
